import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Download, FileText, ArrowUpRight, Beaker, Globe2, Leaf, Fish, Beef, Bird, Dog, Stethoscope, Rabbit, Hexagon, Dumbbell, HeartPulse, Sprout, Recycle, Rocket, BookOpen } from "lucide-react";
import DownloadGate from "@/components/DownloadGate";
import { useLocale } from "@/i18n/useLocale";

const COMPANY_BROCHURE_URL = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "")}/api/resources/company-brochure.pdf`;

const BROCHURES = [
  { id: "e18", title: "Phycocyanin E18", subtitle_en: "Food Grade · Beverage, confectionery, dairy", tagKey: "tag_food", accent: "from-brand-teal/15 to-brand-green/10" },
  { id: "e25", title: "Phycocyanin E25", subtitle_en: "Cosmetic / Nutraceutical Grade · Premium skincare & functional", tagKey: "tag_premium", accent: "from-brand-gold/20 to-brand-cream" },
  { id: "e30", title: "Phycocyanin E30", subtitle_en: "Pharma / Research Grade · Clinical & advanced bioactive", tagKey: "tag_research", accent: "from-brand-green/15 to-brand-cream" },
  { id: "e40", title: "Phycocyanin E40", subtitle_en: "Advanced Technical Grade · Diagnostics, IVD, fluorescence", tagKey: "tag_advanced", accent: "from-brand-black/90 to-brand-teal/40 text-brand-cream" },
];

const USECASES = [
  { id: "food-beverage", title: "Food & Beverage", subtitle: "Natural blue colour systems and functional formats", icon: Globe2, group: "Human" },
  { id: "cosmetics", title: "Cosmetics & Personal Care", subtitle: "Premium skincare, K-Beauty and natural blue actives", icon: Leaf, group: "Human" },
  { id: "nutraceuticals", title: "Nutraceuticals & Supplements", subtitle: "Functional supplements, recovery and clean-label", icon: Beaker, group: "Human" },
  { id: "pharma-diagnostics", title: "Pharma & Diagnostics", subtitle: "Advanced technical grade for high-spec workflows", icon: FileText, group: "Human" },
  { id: "sports-functional-nutrition", title: "Sports & Functional Nutrition", subtitle: "Clean-label colour and recovery positioning in performance formats", icon: Dumbbell, group: "Human" },
  { id: "clinical-medical-nutrition", title: "Clinical & Medical Nutrition", subtitle: "Antioxidant and immune-support inputs for clinical-nutrition", icon: HeartPulse, group: "Human" },
  { id: "aquaculture", title: "Aquaculture", subtitle: "Fish, shrimp and ornamental species — pigmentation, growth, immunity", icon: Fish, group: "Animal" },
  { id: "cattle-livestock", title: "Cattle & Livestock Feed", subtitle: "Dairy, beef and ruminant protein-dense supplementation", icon: Beef, group: "Animal" },
  { id: "poultry", title: "Poultry Feed", subtitle: "Egg-yolk pigmentation, meat quality and immune support", icon: Bird, group: "Animal" },
  { id: "pet-nutrition", title: "Pet & Companion Animal", subtitle: "Premium kibble, treats and functional formats", icon: Dog, group: "Animal" },
  { id: "equine", title: "Equine Nutrition", subtitle: "Recovery, condition and antioxidant support for performance horses", icon: Rabbit, group: "Animal" },
  { id: "apiculture", title: "Apiculture & Bee Nutrition", subtitle: "Supplementary protein and micronutrient support for managed colonies", icon: Hexagon, group: "Animal" },
  { id: "animal-health", title: "Veterinary & Animal Health", subtitle: "Recovery, immunity and clinical-nutrition support", icon: Stethoscope, group: "Animal" },
  { id: "biofertilizer-agriculture", title: "Biofertilizer & Agriculture", subtitle: "Cyanobacterial biomass for soil health and sustainable crop nutrition", icon: Sprout, group: "Industrial" },
  { id: "bioplastics-textiles", title: "Bioplastics & Sustainable Textiles", subtitle: "Algal feedstock and natural-blue dye for renewable materials", icon: Recycle, group: "Industrial" },
  { id: "space-defense", title: "Space & Defense Nutrition", subtitle: "Compact, resilient nutrition for closed-loop and extreme environments", icon: Rocket, group: "Industrial" },
];

const FACTSHEETS = [
  { id: "india-origin", title: "Why India", subtitle: "Climate, workforce, trade-agreement edge" },
  { id: "compliance", title: "Compliance & Documentation", subtitle: "Certifications and dossier framework for July 2027" },
  { id: "sustainability", title: "Sustainability Pledge", subtitle: "Water, carbon, solar, land use, worker welfare" },
];

const Resources = () => {
  const { t } = useTranslation();
  const { L } = useLocale();
  const [gateResource, setGateResource] = useState(null);

  const openGate = (kind, item) => {
    setGateResource({ kind, id: item.id, title: item.title });
  };
  const closeGate = () => setGateResource(null);

  const groupLabel = (g) =>
    g === "Human" ? t("resources_page.group_human")
    : g === "Animal" ? t("resources_page.group_animal")
    : t("resources_page.group_industrial");

  return (
    <div data-testid="page-resources" className="pt-[72px]">
      {/* HERO */}
      <section className="bg-brand-black text-brand-cream relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-25" />
        <div className="container-wide py-20 md:py-28 relative">
          <p className="eyebrow-light mb-5">◦  {t("resources_page.eyebrow")}</p>
          <h1 className="font-display text-4xl sm:text-5xl md:text-7xl tracking-tight leading-[1.05] max-w-4xl">
            {t("resources_page.title_a")} <span className="italic text-brand-gold">{t("resources_page.title_b")}</span>
          </h1>
          <p className="mt-6 text-brand-cream/75 max-w-2xl leading-relaxed text-sm md:text-base">{t("resources_page.intro")}</p>
          <div className="mt-8 flex flex-wrap items-center gap-3 text-[11px] font-mono uppercase tracking-[0.18em] text-brand-cream/55">
            <span>◦  {t("resources_page.stat_brochures")}</span>
            <span>◦  {t("resources_page.stat_use_cases")}</span>
            <span>◦  {t("resources_page.stat_fact_sheets")}</span>
            <span>◦  {t("resources_page.stat_more")}</span>
          </div>
        </div>
      </section>

      {/* BROCHURES */}
      <section id="brochures" className="container-wide py-16 md:py-24 scroll-mt-24" data-testid="resources-brochures">
        <div className="flex items-end justify-between gap-6 mb-10 md:mb-12 flex-wrap">
          <div>
            <p className="eyebrow mb-4">◦  {t("resources_page.section_01")}</p>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl text-brand-black leading-tight tracking-tight max-w-2xl">
              {t("resources_page.section_01_h")} <span className="italic text-brand-teal">{t("resources_page.section_01_h_italic")}</span>
            </h2>
          </div>
          <p className="font-mono text-[11px] uppercase tracking-widest text-brand-black/55 max-w-xs">{t("resources_page.section_01_blurb")}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {/* Company Brochure — master overview (direct download, no gate) */}
          <a
            href={COMPANY_BROCHURE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="resource-brochure-company"
            className="group relative overflow-hidden border border-brand-gold/40 p-7 md:p-8 transition-all duration-300 hover:border-brand-gold hover:-translate-y-0.5 bg-gradient-to-br from-brand-black to-brand-teal/70 text-brand-cream md:col-span-2"
          >
            <div className="flex items-start justify-between gap-4 mb-6">
              <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 border border-brand-gold/50 text-brand-gold">
                {t("resources_page.tag_master")}
              </span>
              <Download size={18} className="opacity-70 group-hover:opacity-100 group-hover:translate-y-0.5 transition" />
            </div>
            <div className="flex items-center gap-3 mb-2">
              <BookOpen size={22} strokeWidth={1.5} className="text-brand-gold" />
              <h3 className="font-display text-2xl md:text-3xl tracking-tight">{t("resources_page.company_brochure_title")}</h3>
            </div>
            <p className="text-sm text-brand-cream/75 leading-relaxed max-w-2xl">{t("resources_page.company_brochure_subtitle")}</p>
            <div className="mt-6 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-brand-gold/85 group-hover:text-brand-gold transition">
              <FileText size={12} /> {t("resources_page.tile_brochure_label")}
            </div>
          </a>

          {BROCHURES.map((b) => (
            <button
              type="button"
              key={b.id}
              onClick={() => openGate("brochure", b)}
              data-testid={`resource-brochure-${b.id}`}
              className={`group relative overflow-hidden border border-brand-green/20 p-7 md:p-8 transition-all duration-300 hover:border-brand-gold hover:-translate-y-0.5 bg-gradient-to-br ${b.accent} text-left`}
            >
              <div className="flex items-start justify-between gap-4 mb-6">
                <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 border border-current/30 opacity-80">
                  {t(`resources_page.${b.tagKey}`)}
                </span>
                <Download size={18} className="opacity-60 group-hover:opacity-100 group-hover:translate-y-0.5 transition" />
              </div>
              <h3 className="font-display text-2xl md:text-3xl tracking-tight mb-2">{b.title}</h3>
              <p className="text-sm opacity-75 leading-relaxed">{b.subtitle_en}</p>
              <div className="mt-6 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] opacity-70 group-hover:opacity-100 transition">
                <FileText size={12} /> {t("resources_page.tile_brochure_label")}
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* USE CASES */}
      <section id="usecases" className="bg-brand-cream border-y border-brand-green/15 scroll-mt-24" data-testid="resources-usecases">
        <div className="container-wide py-16 md:py-24">
          <div className="flex items-end justify-between gap-6 mb-10 md:mb-12 flex-wrap">
            <div>
              <p className="eyebrow mb-4">◦  {t("resources_page.section_02")}</p>
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl text-brand-black leading-tight tracking-tight max-w-2xl">
                {t("resources_page.section_02_h")} <span className="italic text-brand-teal">{t("resources_page.section_02_h_italic")}</span>
              </h2>
            </div>
            <p className="font-mono text-[11px] uppercase tracking-widest text-brand-black/55 max-w-xs">{t("resources_page.section_02_blurb")}</p>
          </div>

          {["Human", "Animal", "Industrial"].map((group) => (
            <div key={group} className="mb-10 md:mb-12 last:mb-0">
              <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-brand-gold mb-5 flex items-center gap-3">
                <span className="h-px w-8 bg-brand-gold/40" />
                {groupLabel(group)}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
                {USECASES.filter((u) => u.group === group).map((u) => {
                  const Icon = u.icon;
                  return (
                    <button
                      type="button"
                      key={u.id}
                      onClick={() => openGate("usecase", u)}
                      data-testid={`resource-usecase-${u.id}`}
                      className="group bg-white border border-brand-green/15 p-6 md:p-7 hover:border-brand-gold hover:-translate-y-0.5 transition-all duration-300 flex flex-col text-left"
                    >
                      <div className="w-11 h-11 border border-brand-green/30 flex items-center justify-center text-brand-teal mb-5">
                        <Icon size={18} />
                      </div>
                      <h3 className="font-display text-xl text-brand-black mb-2 leading-tight">{u.title}</h3>
                      <p className="text-sm text-brand-black/65 leading-relaxed flex-1">{u.subtitle}</p>
                      <div className="mt-6 pt-5 border-t border-brand-green/10 flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.2em] text-brand-black/55 group-hover:text-brand-gold transition">
                        <span>{t("resources_page.tile_usecase_label")}</span>
                        <Download size={14} />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FACT SHEETS */}
      <section id="factsheets" className="container-wide py-16 md:py-24 scroll-mt-24" data-testid="resources-factsheets">
        <div className="flex items-end justify-between gap-6 mb-10 md:mb-12 flex-wrap">
          <div>
            <p className="eyebrow mb-4">◦  {t("resources_page.section_03")}</p>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl text-brand-black leading-tight tracking-tight max-w-2xl">
              {t("resources_page.section_03_h")} <span className="italic text-brand-teal">{t("resources_page.section_03_h_italic")}</span>
            </h2>
          </div>
          <p className="font-mono text-[11px] uppercase tracking-widest text-brand-black/55 max-w-xs">{t("resources_page.section_03_blurb")}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {FACTSHEETS.map((f, i) => (
            <button
              type="button"
              key={f.id}
              onClick={() => openGate("factsheet", f)}
              data-testid={`resource-factsheet-${f.id}`}
              className="group bg-white border border-brand-green/15 p-7 hover:border-brand-gold hover:-translate-y-0.5 transition-all duration-300 text-left"
            >
              <p className="font-mono text-[10px] uppercase tracking-widest text-brand-gold mb-5">FS · 0{i + 1}</p>
              <h3 className="font-display text-2xl text-brand-black mb-3 leading-tight">{f.title}</h3>
              <p className="text-sm text-brand-black/65 leading-relaxed">{f.subtitle}</p>
              <div className="mt-6 pt-5 border-t border-brand-green/10 flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.2em] text-brand-black/55 group-hover:text-brand-gold transition">
                <span>{t("resources_page.tile_factsheet_label")}</span>
                <Download size={14} />
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* JOURNAL + CERTIFICATIONS */}
      <section id="more" className="bg-brand-cream border-t border-brand-green/15 scroll-mt-24" data-testid="resources-more">
        <div className="container-wide py-16 md:py-24">
          <div className="flex items-end justify-between gap-6 mb-10 md:mb-12 flex-wrap">
            <div>
              <p className="eyebrow mb-4">◦  {t("resources_page.section_04")}</p>
              <h2 className="font-display text-3xl sm:text-4xl md:text-5xl text-brand-black leading-tight tracking-tight max-w-2xl">
                {t("resources_page.section_04_h")} <span className="italic text-brand-teal">{t("resources_page.section_04_h_italic")}</span>
              </h2>
            </div>
            <p className="font-mono text-[11px] uppercase tracking-widest text-brand-black/55 max-w-xs">{t("resources_page.section_04_blurb")}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            <Link
              to={L("/journal")}
              data-testid="resources-link-journal"
              className="group bg-white border border-brand-green/15 p-7 hover:border-brand-gold hover:-translate-y-0.5 transition-all duration-300 flex flex-col"
            >
              <p className="font-mono text-[10px] uppercase tracking-widest text-brand-gold mb-5">{t("resources_page.journal_card_eyebrow")}</p>
              <h3 className="font-display text-2xl text-brand-black mb-3 leading-tight">{t("resources_page.journal_card_title")}</h3>
              <p className="text-sm text-brand-black/65 leading-relaxed flex-1">{t("resources_page.journal_card_body")}</p>
              <div className="mt-6 pt-5 border-t border-brand-green/10 flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.2em] text-brand-black/55 group-hover:text-brand-gold transition">
                <span>{t("resources_page.journal_card_cta")}</span>
                <ArrowUpRight size={14} />
              </div>
            </Link>
            <Link
              to={L("/certifications")}
              data-testid="resources-link-certifications"
              className="group bg-white border border-brand-green/15 p-7 hover:border-brand-gold hover:-translate-y-0.5 transition-all duration-300 flex flex-col"
            >
              <p className="font-mono text-[10px] uppercase tracking-widest text-brand-gold mb-5">{t("resources_page.certs_card_eyebrow")}</p>
              <h3 className="font-display text-2xl text-brand-black mb-3 leading-tight">{t("resources_page.certs_card_title")}</h3>
              <p className="text-sm text-brand-black/65 leading-relaxed flex-1">{t("resources_page.certs_card_body")}</p>
              <div className="mt-6 pt-5 border-t border-brand-green/10 flex items-center justify-between text-[10px] font-mono uppercase tracking-[0.2em] text-brand-black/55 group-hover:text-brand-gold transition">
                <span>{t("resources_page.certs_card_cta")}</span>
                <ArrowUpRight size={14} />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="bg-brand-black text-brand-cream">
        <div className="container-wide py-16 md:py-20 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
          <div>
            <p className="eyebrow-light mb-4">◦  {t("resources_page.cta_eyebrow")}</p>
            <h2 className="font-display text-3xl md:text-4xl tracking-tight leading-tight max-w-lg">
              {t("resources_page.cta_title_a")} <span className="italic text-brand-gold">{t("resources_page.cta_title_b")}</span>
            </h2>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 md:justify-end">
            <Link
              to={L("/reserve-allocation")}
              data-testid="resources-cta-reserve"
              className="inline-flex items-center justify-center gap-2 bg-brand-gold text-brand-black px-6 py-4 text-xs uppercase tracking-[0.2em] font-medium hover:bg-brand-cream transition min-h-[48px]"
            >
              {t("nav.reserve_cta")} <ArrowUpRight size={14} />
            </Link>
            <Link
              to={L("/distributor-portal")}
              data-testid="resources-cta-distributor"
              className="inline-flex items-center justify-center gap-2 border border-brand-cream/30 text-brand-cream px-6 py-4 text-xs uppercase tracking-[0.2em] hover:bg-brand-cream/10 transition min-h-[48px]"
            >
              {t("nav.distributor")}
            </Link>
          </div>
        </div>
      </section>

      <DownloadGate
        open={!!gateResource}
        onClose={closeGate}
        resource={gateResource}
      />
    </div>
  );
};

export default Resources;
